import React, { useContext } from 'react';
import styled from 'styled-components';
import Modal from '@atlaskit/modal-dialog';

import Row from '../../../../components/Common/Row';
import SideBar from './SideBar';
import MetricPopupConstants from './constants';
import BottomBar from './BottomBar';
import TopBarInner from './TopBarInner';
import MetricForm from './MetricForm';
import Flash from '../../../../components/Flash';
import useWindowSize from '../../../../hooks/useWindowSize';
import CloseButton from '../../../../components/CloseButton';
import Title from './Title';
import { MetricPopupPortalIdsContext } from './MetricPopupPortalIdsContext';
import MEDIA from '../../../../media';

const PopupInnerDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const MetricListDiv = styled.div`
  width: 280px;
  border-right: 1px solid var(--color-grey-8);

  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContentOuterDiv = styled.div`
  height: 100%;
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainContentInnerDiv = styled(Row)`
  flex: 1;
  justify-content: space-between;
`;

const MainContentHeader = styled.div`
  padding: ${MetricPopupConstants.PADDING};
  border-bottom: 1px solid var(--color-grey-8);

  @media ${MEDIA.QUERIES.SMALL_HEIGHT} {
    padding: ${MetricPopupConstants.PADDING_SMALL};
  }
`;

const Form = styled.div`
  width: 525px;
  border-right: 1px solid var(--color-grey-8);
  overflow-y: auto;
`;

const PreviewDiv = styled.div`
  padding: ${MetricPopupConstants.PADDING};
  flex: 1;
  height: calc(${MetricPopupConstants.POPUP_HEIGHT} - 162px);
  @media ${MEDIA.QUERIES.SMALL_HEIGHT} {
    height: calc(${MetricPopupConstants.POPUP_HEIGHT_SMALL} - 116px);
  }
  overflow-y: scroll;

  @media ${MEDIA.QUERIES.SMALL_HEIGHT} {
    padding: ${MetricPopupConstants.PADDING_SMALL};
  }
`;

const MetricPopup = ({
  close,
  selectedMetric,
  setSelectedMetric,
  id,
  setId,
  isFlashVisible,
  setIsFlashVisible,
  flashMessage,
  showFlash,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  mode,
  forcedDataset,
}: {
  close: () => void;
  selectedMetric?: Metrics.NormalMetric | Metrics.CompoundMetric;
  setSelectedMetric?: React.Dispatch<
    React.SetStateAction<
      Metrics.NormalMetric | Metrics.CompoundMetric | undefined
    >
  >;
  id: string;
  setId: React.Dispatch<React.SetStateAction<string>>;
  isFlashVisible: boolean;
  setIsFlashVisible: React.Dispatch<React.SetStateAction<boolean>>;
  flashMessage: string;
  showFlash: (message: string) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  mode: 'Metric' | 'CompoundMetric';
  forcedDataset?: string;
}) => {
  const { previewDivId, coreToggleDivId } = useContext(
    MetricPopupPortalIdsContext,
  );
  const { windowWidth, windowHeight } = useWindowSize();

  return (
    <Modal
      width={
        windowWidth <= MEDIA.MEASUREMENTS.WIDTH_BREAKPOINT_MEDIUM &&
        windowWidth > MEDIA.MEASUREMENTS.WIDTH_BREAKPOINT_SMALL
          ? '90vw'
          : windowWidth <= MEDIA.MEASUREMENTS.WIDTH_BREAKPOINT_SMALL
            ? '95vw'
            : '75vw'
      }
      height={
        windowHeight <= MEDIA.MEASUREMENTS.HEIGHT_BREAKPOINT
          ? MetricPopupConstants.POPUP_HEIGHT_SMALL
          : MetricPopupConstants.POPUP_HEIGHT
      }
      onClose={close}
      shouldScrollInViewport={false}
      autoFocus={false}
    >
      <Flash
        isFlashVisible={isFlashVisible}
        setIsFlashVisible={setIsFlashVisible}
        message={flashMessage}
        type="info"
      />
      <PopupInnerDiv>
        <Row style={{ flex: 1 }}>
          {!!setSelectedMetric && (
            <MetricListDiv>
              <SideBar
                hasUnsavedChanges={hasUnsavedChanges}
                selectedMetric={selectedMetric}
                setSelectedMetric={setSelectedMetric}
                setId={setId}
                mode={mode}
              />
            </MetricListDiv>
          )}
          <MainContentOuterDiv>
            <MainContentHeader>
              <TopBarInner>
                <Title
                  selectedMetric={selectedMetric}
                  hasUnsavedChanges={hasUnsavedChanges}
                />
                <Row centerAlign>
                  <div id={coreToggleDivId} />
                  <CloseButton close={close} />
                </Row>
              </TopBarInner>
            </MainContentHeader>
            <MainContentInnerDiv key={id}>
              <Form>
                <MetricForm
                  selectedMetric={selectedMetric}
                  id={id}
                  showFlash={showFlash}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  mode={mode}
                  forcedDataset={forcedDataset}
                />
              </Form>
              <PreviewDiv id={previewDivId} />
            </MainContentInnerDiv>
          </MainContentOuterDiv>
        </Row>
        <BottomBar close={close} />
      </PopupInnerDiv>
    </Modal>
  );
};

export default MetricPopup;
